// Wave 20.5 — /drops marketing page.
//
// Entry-tier product pitch page. Aero AI is the main product; Drops
// are the small viral loop that pulls new riders in. Structure:
//   - Hero: bold single-sentence pitch + primary CTA
//   - 3-step how it works (Record → Tap Drop → Share)
//   - Fake mock of a Drop Link so visitors get the concept without
//     needing a real link
//   - Bottom: App Store / Play badges + "aero.chat is more than
//     Drops" callout linking to the main product

import React from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  CssBaseline,
  Stack,
  ThemeProvider,
  Typography,
} from '@mui/material';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import { Link as RouterLink } from 'react-router-dom';
import AppHeader from './components/AppHeader';
import DropStoryboard from './components/DropStoryboard';
import { theme, colors } from './theme';

export default function Drops() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ backgroundColor: colors.bg, minHeight: '100vh', color: colors.text }}>
        <AppHeader />

        {/* Wave 20.20 — Hero. Compressed text on the left + the
            animated storyboard on the right so the animation is
            visible in the initial viewport instead of buried
            below a full-height text block. Stacks vertically on
            mobile with the storyboard second. */}
        <Box
          sx={{
            py: { xs: 4, md: 6 },
            px: 3,
            background: `radial-gradient(1200px 400px at 50% 0%, ${colors.brand}18, transparent 70%)`,
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: { xs: 4, md: 6 },
                alignItems: 'center',
              }}
            >
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Chip
                  label="NEW"
                  size="small"
                  sx={{
                    backgroundColor: colors.brand,
                    color: '#0A100C',
                    fontWeight: 800,
                    letterSpacing: '0.14em',
                    mb: 2,
                  }}
                />
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 900,
                    letterSpacing: '-0.03em',
                    fontSize: { xs: '2.25rem', md: '3.25rem' },
                    lineHeight: 1.05,
                  }}
                >
                  Drop your friends.
                  <br />
                  <Box component="span" sx={{ color: colors.brand }}>
                    Send links and challenge others.
                  </Box>
                </Typography>
                <Typography
                  sx={{
                    mt: 2,
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    color: colors.textMuted,
                    maxWidth: 520,
                    mx: { xs: 'auto', md: 0 },
                  }}
                >
                  Record. We detect peaks. You drop a link that expires when you say —
                  30 min, 24 hours, a week, or never.
                </Typography>
                {/* Expiration is a real feature of the drop link — spell
                    it out visually so it's obvious the sender is in
                    control of who can see it and for how long. */}
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    mt: 2.5,
                    flexWrap: 'wrap',
                    gap: 1,
                    justifyContent: { xs: 'center', md: 'flex-start' },
                  }}
                >
                  <Chip
                    label="⏱ Expires"
                    size="small"
                    sx={{
                      backgroundColor: `${colors.brand}18`,
                      color: colors.brand,
                      fontWeight: 800,
                      letterSpacing: '0.08em',
                      border: `1px solid ${colors.brand}44`,
                    }}
                  />
                  {['30 min', '24 h', '7 d', 'never'].map((label) => (
                    <Chip
                      key={label}
                      label={label}
                      size="small"
                      sx={{
                        backgroundColor: 'transparent',
                        color: colors.textMuted,
                        fontWeight: 700,
                        border: `1px solid ${colors.border}`,
                      }}
                    />
                  ))}
                </Stack>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  justifyContent={{ xs: 'center', md: 'flex-start' }}
                  sx={{ mt: 3 }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<SmartphoneIcon />}
                    href="#get-app"
                    sx={{
                      backgroundColor: colors.brand,
                      color: '#0A100C',
                      fontWeight: 800,
                      px: 4,
                      py: 1.5,
                      '&:hover': { backgroundColor: colors.brand, opacity: 0.9 },
                    }}
                  >
                    Get the app
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    component={RouterLink}
                    to="/"
                    sx={{ color: colors.text, borderColor: colors.border, px: 4, py: 1.5 }}
                  >
                    See Aero AI
                  </Button>
                </Stack>
              </Box>

              <DropStoryboard compact />
            </Box>
          </Container>
        </Box>

        {/* Get-app CTA */}
        <Box id="get-app" sx={{ py: { xs: 8, md: 12 }, px: 3, backgroundColor: colors.surface, textAlign: 'center' }}>
          <Container maxWidth="sm">
            <Typography variant="h3" sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '2.5rem' } }}>
              Start dropping your friends.
            </Typography>
            <Typography sx={{ mt: 2, color: colors.textMuted, fontSize: '1.05rem' }}>
              Mobile app coming soon.
            </Typography>
          </Container>
        </Box>

        {/* Main product callout */}
        <Box sx={{ py: { xs: 6, md: 10 }, px: 3, textAlign: 'center' }}>
          <Container maxWidth="md">
            <Typography variant="overline" sx={{ color: colors.textMuted, letterSpacing: '0.2em' }}>
              aero.chat is more than Drops
            </Typography>
            <Typography sx={{ mt: 1, fontSize: { xs: '1.1rem', md: '1.35rem' }, color: colors.text, mb: 3 }}>
              The main product is <strong>Aero AI</strong> — aerodynamics
              measurements and coaching with a single camera.
            </Typography>
            <Button
              component={RouterLink}
              to="/"
              variant="text"
              sx={{ color: colors.brand, fontWeight: 700 }}
            >
              See Aero AI →
            </Button>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

